import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  PackageCheck, TruckIcon, FileText, AlertTriangle, Receipt, Wallet,
  Gavel, ShieldCheck, ListChecks, Factory, TrendingUp, ArrowUpRight, Sparkles,
  FlaskConical, PackagePlus, Package, Microscope, FileSearch, Wrench, ShoppingCart,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { formatCurrency, formatNumber, ROLE_LABELS } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import ApprovalQueueWidget from "@/components/ApprovalQueueWidget";

const ACCENT_STYLE = {
  slate: "bg-slate-50 text-slate-600",
  blue: "bg-blue-50 text-blue-600",
  amber: "bg-amber-50 text-amber-600",
  violet: "bg-violet-50 text-violet-600",
  emerald: "bg-emerald-50 text-emerald-600",
  rose: "bg-rose-50 text-rose-600",
};

const KPI = ({ label, value, sub, icon: Icon, accent = "slate", testid, to }) => {
  const card = (
    <Card className={`border-slate-200 transition-shadow ${to ? "hover:shadow-md hover:border-slate-300 cursor-pointer" : ""}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">{label}</div>
            <div className="text-3xl font-bold text-slate-900 mt-2 font-[Manrope]">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-1.5">{sub}</div>}
          </div>
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${ACCENT_STYLE[accent] || ACCENT_STYLE.slate}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {to && (
          <div className="mt-3 flex items-center gap-1 text-[11px] font-semibold text-blue-600">
            View list <ArrowUpRight className="h-3 w-3" />
          </div>
        )}
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block focus:outline-none focus:ring-2 focus:ring-slate-300 rounded-lg">{card}</Link> : card;
};

const COLORS = ["#0f172a", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#64748b"];
const TOOLTIP_STYLE = { borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 };
const AXIS_STYLE = { stroke: "#94a3b8", fontSize: 11 };
const LEGEND_STYLE = { fontSize: 11 };
const CHART_HEIGHT_SM = { width: "100%", height: 240 };
const CHART_HEIGHT_MD = { width: "100%", height: 260 };
const LINE_DOT = { r: 4, fill: "#0f172a" };
const currencyTickFormatter = (v) => `₹${Math.round(v / 1000)}K`;
const currencyTooltipFormatter = (v) => [formatCurrency(v), "Amount"];

export default function DashboardPage() {
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, modulesAccess } = useAuth();

  const isExec = user?.role === "admin" || user?.role === "dept_head";

  const canAccess = (moduleKey) => {
    if (!user) return false;
    if (isExec) return true;
    return (modulesAccess || []).includes(moduleKey);
  };

  useEffect(() => {
    let mounted = true;
    Promise.all([api.get("/dashboard/kpis"), api.get("/dashboard/alerts")])
      .then(([k, a]) => {
        if (!mounted) return;
        setKpis(k.data);
        setAlerts(a.data.alerts || []);
      })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }
  if (!kpis) return null;

  const stageData = Object.entries(kpis.sales_pipeline?.by_stage || {}).map(([k, v]) => ({ name: k, value: v }));
  const expTrend = kpis.expense_trend || [];
  const personData = kpis.tasks_by_person || [];
  const departmentData = kpis.departments || [];
  const showExpenseTrend = canAccess("expenses") && expTrend.length > 0;
  const showSalesChart = canAccess("sales_conversion");
  const showTasksCharts = canAccess("tasks");
  const showCertCard = canAccess("certificates") || (canAccess("purchase_orders") && kpis.pbg);
  const roleLabel = (user?.role && ROLE_LABELS?.[user.role]) || "Operations";
  const headline = isExec ? "Owner Console" : `${roleLabel} Dashboard`;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{headline}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">Hello, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isExec
              ? "Here's what's happening across HARDIK MEDITECH today."
              : `Your ${roleLabel.toLowerCase()} view — only the modules and alerts you own.`}
          </p>
        </div>
        {isExec && (
          <div className="flex items-center gap-2">
            <Link to="/ai-report">
              <Button className="bg-slate-900 hover:bg-slate-800" data-testid="generate-ai-report-cta">
                <Sparkles className="h-4 w-4 mr-2 text-amber-300" /> Generate AI Weekly Report
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* KPI Grid — each card only renders if the user has access to that module */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="kpi-grid">
        {canAccess("purchase_orders") && kpis.po && (
          <KPI label="Active POs" value={kpis.po.active} sub={`${kpis.po.total} total · ${kpis.po.delayed} delayed`} icon={FileText} accent="blue" testid="kpi-po-active" to="/m/purchase_orders" />
        )}
        {canAccess("dispatch") && kpis.dispatch_pending !== undefined && (
          <KPI label="Dispatch Pending" value={kpis.dispatch_pending} sub="Awaiting delivery" icon={TruckIcon} accent="amber" testid="kpi-dispatch-pending" to="/m/dispatch" />
        )}
        {canAccess("supply_sheet") && kpis.installation_pending !== undefined && (
          <KPI label="Installation Pending" value={kpis.installation_pending} sub="Field engineer queue" icon={PackageCheck} accent="violet" testid="kpi-installation-pending" to="/m/supply_sheet" />
        )}
        {canAccess("production") && kpis.production_stock !== undefined && (
          <KPI label="Production Stock" value={formatNumber(kpis.production_stock)} sub="Units in inventory" icon={Factory} accent="emerald" testid="kpi-stock" to="/m/production" />
        )}
        {canAccess("expenses") && kpis.monthly_expenses !== undefined && (
          <KPI label="Monthly Expenses" value={formatCurrency(kpis.monthly_expenses)} sub="This month" icon={Receipt} accent="rose" testid="kpi-monthly-expenses" to="/m/expenses" />
        )}
        {canAccess("sales_conversion") && kpis.sales_pipeline && (
          <KPI label="Sales Pipeline" value={formatCurrency(kpis.sales_pipeline.value)} sub="Open leads value" icon={TrendingUp} accent="emerald" testid="kpi-sales-pipeline" to="/m/sales_conversion" />
        )}
        {canAccess("sales") && kpis.sales_count !== undefined && (
          <KPI label="Sales Records" value={kpis.sales_count} sub={`${kpis.sales_won || 0} won`} icon={ShoppingCart} accent="blue" testid="kpi-sales" to="/m/sales" />
        )}
        {canAccess("tenders_published") && kpis.tenders && (
          <KPI label="Open Tenders" value={kpis.tenders.open} sub={`${kpis.tenders.bidding} actively bidding`} icon={Gavel} accent="blue" testid="kpi-tenders" to="/m/tenders_published" />
        )}
        {canAccess("samples") && kpis.samples_pending !== undefined && (
          <KPI label="Samples Pending" value={kpis.samples_pending} sub="Received / under review" icon={FlaskConical} accent="violet" testid="kpi-samples" to="/m/samples" />
        )}
        {canAccess("material_received") && kpis.material_qc_pending !== undefined && (
          <KPI label="Material QC Pending" value={kpis.material_qc_pending} sub="Awaiting QC" icon={PackagePlus} accent="amber" testid="kpi-material" to="/m/material_received" />
        )}
        {canAccess("products") && kpis.products_active !== undefined && (
          <KPI label="Active Products" value={kpis.products_active} sub="In catalogue" icon={Package} accent="emerald" testid="kpi-products" to="/m/products" />
        )}
        {canAccess("rnd") && kpis.rnd_active !== undefined && (
          <KPI label="R&D Projects" value={kpis.rnd_active} sub="Active / approved" icon={Microscope} accent="violet" testid="kpi-rnd" to="/m/rnd" />
        )}
        {canAccess("procurement_quotations") && kpis.quotations_pending !== undefined && (
          <KPI label="Quotations Pending" value={kpis.quotations_pending} sub="Awaiting decision" icon={FileSearch} accent="amber" testid="kpi-quotations" to="/m/procurement_quotations" />
        )}
        {canAccess("engineer_expense") && kpis.engineer_expense_pending !== undefined && (
          <KPI label="Engineer Expenses" value={kpis.engineer_expense_pending} sub="Pending approval" icon={Wrench} accent="rose" testid="kpi-engineer-expense" to="/m/engineer_expense" />
        )}
        {canAccess("tasks") && kpis.tasks && (
          <KPI label={user?.role === "engineer" || user?.role === "staff" ? "My Tasks" : "Pending Tasks"} value={kpis.tasks.pending} sub={`${kpis.tasks.delayed} delayed`} icon={ListChecks} accent="amber" testid="kpi-tasks" to="/m/tasks" />
        )}
      </div>

      {/* Alerts + Cert/PBG card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-2" data-testid="alerts-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Critical Alerts</div>
                <div className="text-base font-semibold text-slate-900 mt-0.5">
                  {isExec ? "Needs your attention" : "Scoped to your modules"}
                </div>
              </div>
              <Badge variant="outline" className="border-red-200 text-red-600 bg-red-50">{alerts.length}</Badge>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
              {alerts.length === 0 && (
                <div className="text-sm text-slate-500 py-8 text-center">All clear. No critical alerts.</div>
              )}
              {alerts.map((a, idx) => (
                <Link
                  key={`${a.module}-${a.record_id}-${a.type}-${idx}`}
                  to={`/m/${a.module}`}
                  className="block p-3 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors"
                  data-testid={`alert-row-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className={`h-4 w-4 mt-0.5 ${a.severity === "danger" ? "text-red-500" : "text-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{a.title}</div>
                      <div className="text-xs text-slate-500 truncate">{a.subtitle}</div>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-slate-400" />
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>

        {showCertCard && (
          <Card className="border-slate-200" data-testid="certificate-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Certificates & PBG</div>
              </div>
              {canAccess("certificates") && kpis.certificates && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Certificate Expiry</div>
                  <div className="space-y-2">
                    {[
                      { l: "Already Expired", v: kpis.certificates.expired, c: "bg-red-500" },
                      { l: "Within 30 days", v: kpis.certificates.in_30d, c: "bg-amber-500" },
                      { l: "Within 60 days", v: kpis.certificates.in_60d, c: "bg-yellow-400" },
                      { l: "Within 90 days", v: kpis.certificates.in_90d, c: "bg-emerald-500" },
                    ].map((b) => (
                      <div key={b.l} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${b.c}`} />
                          <span className="text-sm text-slate-700">{b.l}</span>
                        </div>
                        <span className="text-sm font-bold text-slate-900">{b.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canAccess("purchase_orders") && kpis.pbg && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">PBG (Bank Guarantee) Expiry</div>
                  <div className="space-y-2">
                    {[
                      { l: "Already Expired", v: kpis.pbg.expired, c: "bg-red-500" },
                      { l: "Within 30 days", v: kpis.pbg.in_30d, c: "bg-amber-500" },
                      { l: "Within 60 days", v: kpis.pbg.in_60d, c: "bg-yellow-400" },
                    ].map((b) => (
                      <div key={b.l} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${b.c}`} />
                          <span className="text-sm text-slate-700">{b.l}</span>
                        </div>
                        <span className="text-sm font-bold text-slate-900">{b.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canAccess("certificates") && (
                <Link to="/m/certificates" className="block mt-4 text-xs font-semibold text-blue-600 hover:text-blue-700">
                  View certificate registry →
                </Link>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Charts — only shown for divisions that own the underlying modules */}
      {(showExpenseTrend || showSalesChart) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {showExpenseTrend && (
            <Card className={`border-slate-200 ${showSalesChart ? "lg:col-span-2" : "lg:col-span-3"}`} data-testid="expense-trend-card">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Expense Trend</div>
                <div className="text-base font-semibold text-slate-900 mb-4">Last 6 months</div>
                <div style={CHART_HEIGHT_MD}>
                  <ResponsiveContainer>
                    <LineChart data={expTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" {...AXIS_STYLE} />
                      <YAxis {...AXIS_STYLE} tickFormatter={currencyTickFormatter} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} formatter={currencyTooltipFormatter} />
                      <Line type="monotone" dataKey="amount" stroke="#0f172a" strokeWidth={2.5} dot={LINE_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
          {showSalesChart && (
            <Card className={`border-slate-200 ${showExpenseTrend ? "" : "lg:col-span-3"}`} data-testid="pipeline-stage-card">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Sales Pipeline</div>
                <div className="text-base font-semibold text-slate-900 mb-4">By stage</div>
                <div style={CHART_HEIGHT_SM}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={stageData.length ? stageData : [{ name: "No data", value: 1 }]}
                        cx="50%" cy="50%" innerRadius={48} outerRadius={80} dataKey="value" paddingAngle={2}
                      >
                        {stageData.map((entry, i) => (
                          <Cell key={entry.name || `stage-${i}`} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Legend wrapperStyle={LEGEND_STYLE} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {(isExec || showTasksCharts) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isExec && <ApprovalQueueWidget />}
          {showTasksCharts && personData.length > 0 && (
            <Card className="border-slate-200" data-testid="task-person-card">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Pending Tasks</div>
                <div className="text-base font-semibold text-slate-900 mb-4">
                  {user?.role === "engineer" || user?.role === "staff" ? "Your assignments" : "By person"}
                </div>
                <div style={CHART_HEIGHT_MD}>
                  <ResponsiveContainer>
                    <BarChart data={personData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                      <XAxis type="number" {...AXIS_STYLE} />
                      <YAxis dataKey="person" type="category" {...AXIS_STYLE} width={120} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Bar dataKey="count" fill="#0f172a" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
          {isExec && departmentData.length > 0 && (
            <Card className="border-slate-200" data-testid="department-card">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Department Load</div>
                <div className="text-base font-semibold text-slate-900 mb-4">All tasks by department</div>
                <div style={CHART_HEIGHT_MD}>
                  <ResponsiveContainer>
                    <BarChart data={departmentData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="department" {...AXIS_STYLE} />
                      <YAxis {...AXIS_STYLE} />
                      <Tooltip contentStyle={TOOLTIP_STYLE} />
                      <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
